// ---------------------------------------------------------------------------
// resources-data.ts
// Comprehensive drone industry resource directory for the africanDRONE map.
// African companies and organisations are listed FIRST in every category.
// Every entity from the "Drones for Good" guide (400 Feet) is included.
// ---------------------------------------------------------------------------

export interface ResourceEntry {
  name: string;
  url: string;
  description?: string;
  hqCountry?: string; // ISO 3166-1 alpha-2 (e.g. 'US', 'CN', 'DE')
}

export interface ResourceSection {
  id: string;
  label: string;
  icon: string; // emoji
  entries: ResourceEntry[];
}

export interface NewsFeed {
  id: string;
  label: string;
  url: string;
  color: string;
}

// ============================================================================
// 1. SAFETY SECTIONS
// ============================================================================

export const SAFETY_SECTIONS: ResourceSection[] = [
  {
    id: 'airspace-maps',
    label: 'Airspace Maps & Apps',
    icon: '\uD83D\uDDFA\uFE0F',
    entries: [
      // ── African first ──
      {
        name: 'PDASA Drone Map',
        url: 'https://drones.org.za/drone-map-v2-0/',
        description: 'South African no-fly zone advisory map',
        hqCountry: 'ZA',
      },
      {
        name: 'DroneSafetyMap',
        url: 'https://dronesafetymap.com/',
        description: 'Free airspace map covering 155+ countries',
        hqCountry: 'GB',
      },
      {
        name: 'ADS-B Exchange',
        url: 'https://adsbexchange.com/',
        description: 'Unfiltered real-time aircraft tracking feed',
        hqCountry: 'US',
      },
      {
        name: 'Flightradar24',
        url: 'https://www.flightradar24.com/',
        description: 'Global real-time flight tracking platform',
        hqCountry: 'SE',
      },
      {
        name: 'OpenSky Network',
        url: 'https://opensky-network.org/',
        description: 'Open research network for air traffic data',
        hqCountry: 'CH',
      },
      {
        name: 'B4UFLY (FAA)',
        url: 'https://www.faa.gov/uas/getting_started/b4ufly',
        description: 'FAA airspace awareness tool for drone pilots',
        hqCountry: 'US',
      },
      {
        name: 'Aloft (Air Aware)',
        url: 'https://www.aloft.ai/',
        description: 'UTM platform, LAANC, and airspace awareness',
        hqCountry: 'US',
      },
      {
        name: 'AirMap',
        url: 'https://www.airmap.com/',
        description: 'Airspace management and UTM platform',
        hqCountry: 'US',
      },
      {
        name: 'OpenSky (Wing)',
        url: 'https://wing.com/opensky/',
        description: 'Real-time airspace intelligence by Wing',
        hqCountry: 'US',
      },
      {
        name: 'Dronedesk',
        url: 'https://www.dronedesk.io/',
        description: 'UK drone operations management platform',
        hqCountry: 'GB',
      },
    ],
  },
  {
    id: 'parachute-recovery',
    label: 'Parachute Recovery Systems',
    icon: '\uD83E\uDE82',
    entries: [
      {
        name: 'ParaZero',
        url: 'https://parazero.com/',
        description: 'Autonomous SafeAir drone parachute systems',
        hqCountry: 'IL',
      },
      {
        name: 'Fruity Chutes',
        url: 'https://fruitychutes.com/',
        description: 'Parachute systems for drones and UAVs',
        hqCountry: 'US',
      },
      {
        name: 'AVSS',
        url: 'https://avss.co/',
        description: 'ASTM-compliant drone parachute recovery',
        hqCountry: 'CA',
      },
      {
        name: 'Drone Rescue Systems',
        url: 'https://dronerescue.com/',
        description: 'Autonomous parachute systems from Austria',
        hqCountry: 'AT',
      },
      {
        name: 'Indemnis',
        url: 'https://www.indemnis.com/',
        description: 'Nexus ballistic parachute for DJI drones',
        hqCountry: 'US',
      },
      {
        name: 'Skycat',
        url: 'https://www.skycat.pro/',
        description: 'Lightweight spring-based parachute launchers',
        hqCountry: 'FI',
      },
      {
        name: 'Galaxy GRS',
        url: 'https://www.galaxysky.cz/',
        description: 'Ballistic rescue systems for drones and ULs',
        hqCountry: 'CZ',
      },
    ],
  },
  {
    id: 'detect-avoid',
    label: 'Detect & Avoid',
    icon: '\uD83D\uDCE1',
    entries: [
      {
        name: 'Iris Automation',
        url: 'https://www.irisonboard.com/',
        description: 'Computer-vision DAA for BVLOS operations',
        hqCountry: 'US',
      },
      {
        name: 'uAvionix',
        url: 'https://uavionix.com/',
        description: 'Low-SWaP avionics, ADS-B, and Remote ID',
        hqCountry: 'US',
      },
      {
        name: 'Echodyne',
        url: 'https://www.echodyne.com/',
        description: 'Metamaterials radar for DAA and security',
        hqCountry: 'US',
      },
      {
        name: 'Fortem Technologies',
        url: 'https://fortemtech.com/',
        description: 'Counter-UAS radar and DroneHunter systems',
        hqCountry: 'US',
      },
    ],
  },
  {
    id: 'utm',
    label: 'Traffic Management (UTM)',
    icon: '\uD83D\uDEE9\uFE0F',
    entries: [
      {
        name: 'Altitude Angel',
        url: 'https://www.altitudeangel.com/',
        description: 'GuardianUTM cloud-based drone management',
        hqCountry: 'GB',
      },
      {
        name: 'AirMap',
        url: 'https://www.airmap.com/',
        description: 'Airspace management and UTM platform',
        hqCountry: 'US',
      },
      {
        name: 'ANRA Technologies',
        url: 'https://www.anratechnologies.com/',
        description: 'Airspace and mission management for UAS',
        hqCountry: 'US',
      },
      {
        name: 'Unifly',
        url: 'https://www.unifly.aero/',
        description: 'European UTM platform (Terra Drone Group)',
        hqCountry: 'BE',
      },
      {
        name: 'OneSky',
        url: 'https://www.onesky.xyz/',
        description: 'UTM and airspace assessment solutions',
        hqCountry: 'US',
      },
    ],
  },
  {
    id: 'remote-id',
    label: 'Remote ID',
    icon: '\uD83D\uDCF6',
    entries: [
      {
        name: 'uAvionix',
        url: 'https://uavionix.com/',
        description: 'Low-SWaP avionics, ADS-B, and Remote ID',
        hqCountry: 'US',
      },
      {
        name: 'Dronetag',
        url: 'https://www.dronetag.com/',
        description: 'Lightweight Remote ID modules from Prague',
        hqCountry: 'CZ',
      },
      {
        name: 'Thales',
        url: 'https://www.thalesgroup.com/en/markets/aerospace/drone-solutions',
        description: 'ScaleFlyt Remote ID and TopSky UTM',
        hqCountry: 'FR',
      },
    ],
  },
  {
    id: 'training-cert',
    label: 'Training & Certification',
    icon: '\uD83C\uDF93',
    entries: [
      // ── African first ──
      {
        name: 'africanDRONE Academy',
        url: 'https://africandrone.org/academy',
        description: 'Drone journalism and pilot training courses',
        hqCountry: 'ZA',
      },
      {
        name: 'African Drone and Data Academy',
        url: 'https://adda.cired.vt.edu/',
        description: 'UNICEF-powered drone training in Malawi',
        hqCountry: 'MW',
      },
      {
        name: 'Drone Space Kenya',
        url: 'https://dronespace.co.ke/',
        description: 'KCAA-approved drone pilot license training',
        hqCountry: 'KE',
      },
      {
        name: 'Ntsu Drone Academy',
        url: 'https://www.ntsuaviation.com/',
        description: 'SACAA-accredited drone pilot training',
        hqCountry: 'ZA',
      },
      {
        name: 'Drones for Africa',
        url: 'https://www.dronesforafrica.com/',
        description: 'Botswana drone training and STEM education',
        hqCountry: 'BW',
      },
      {
        name: 'KENDRONE',
        url: 'https://www.kendrone.co.ke/',
        description: 'Kenya\'s first KCAA-approved drone org',
        hqCountry: 'KE',
      },
      // ── Global ──
      {
        name: 'DARTdrones',
        url: 'https://www.dartdrones.com/',
        description: 'Nationwide drone training in 40+ US cities',
        hqCountry: 'US',
      },
      {
        name: 'Drone U',
        url: 'https://www.thedroneu.com/',
        description: 'Online and in-person pro drone courses',
        hqCountry: 'US',
      },
      {
        name: 'UAV Coach',
        url: 'https://uavcoach.com/',
        description: 'Part 107 prep, TRUST, and drone training',
        hqCountry: 'US',
      },
      {
        name: 'Women Who Drone',
        url: 'https://www.womenwhodrone.com/',
        description: 'Drone academy and women-in-drones community',
        hqCountry: 'US',
      },
      {
        name: 'Embry-Riddle Aeronautical University',
        url: 'https://erau.edu/degrees/bachelor/uncrewed-autonomous-systems',
        description: 'First accredited UAS degree program in the US',
        hqCountry: 'US',
      },
    ],
  },
];

// ============================================================================
// 2. EQUIPMENT SECTIONS (Hardware, Components, Parts)
// ============================================================================

export const EQUIPMENT_SECTIONS: ResourceSection[] = [
  {
    id: 'consumer-prosumer',
    label: 'Consumer & Prosumer',
    icon: '\uD83C\uDFAE',
    entries: [
      {
        name: 'DJI',
        url: 'https://www.dji.com/',
        description: 'World-leading consumer and enterprise drones',
        hqCountry: 'CN',
      },
      {
        name: 'Autel Robotics',
        url: 'https://www.autelrobotics.com/',
        description: 'EVO series consumer and enterprise drones',
        hqCountry: 'US',
      },
      {
        name: 'Parrot',
        url: 'https://www.parrot.com/',
        description: 'ANAFI series drones for consumer and defense',
        hqCountry: 'FR',
      },
      {
        name: 'Skydio',
        url: 'https://www.skydio.com/',
        description: 'AI-powered autonomous drones (US-made)',
        hqCountry: 'US',
      },
      {
        name: 'HOVERAir',
        url: 'https://hoverair.com/',
        description: 'Pocket-sized self-flying camera drones',
        hqCountry: 'CN',
      },
      {
        name: 'Holy Stone',
        url: 'https://www.holystone.com/',
        description: 'Affordable beginner and hobby drones',
        hqCountry: 'CN',
      },
    ],
  },
  {
    id: 'enterprise-industrial',
    label: 'Enterprise & Industrial',
    icon: '\uD83C\uDFED',
    entries: [
      // ── African first ──
      {
        name: 'Micromek',
        url: 'https://www.micromek.net/',
        description: 'Malawian drone manufacturer — EcoSoar UAV for medical delivery and environmental monitoring',
        hqCountry: 'MW',
      },
      {
        name: 'ALTI Unmanned',
        url: 'https://altiunmanned.com/',
        description: 'Africa\'s largest commercial VTOL drone maker',
        hqCountry: 'ZA',
      },
      {
        name: 'Milkor',
        url: 'https://milkor.com/',
        description: 'South African MALE UAV and defense systems',
        hqCountry: 'ZA',
      },
      {
        name: 'DroneScan',
        url: 'https://www.dronescan.co/',
        description: 'Warehouse inventory drone scanning systems',
        hqCountry: 'ZA',
      },
      {
        name: 'Astral Aerial Solutions',
        url: 'https://astral-aerial.com/',
        description: 'Kenyan drone manufacturer, cargo up to 2000kg',
        hqCountry: 'KE',
      },
      // ── Global ──
      {
        name: 'Flyability',
        url: 'https://www.flyability.com/',
        description: 'Collision-tolerant drones for confined spaces',
        hqCountry: 'CH',
      },
      {
        name: 'Airobotics',
        url: 'https://www.airoboticsdrones.com/',
        description: 'Fully automated drone-in-a-box platform',
        hqCountry: 'IL',
      },
      {
        name: 'Freefly Systems',
        url: 'https://freeflysystems.com/',
        description: 'Astro platform and cinema drone systems',
        hqCountry: 'US',
      },
      {
        name: 'Percepto',
        url: 'https://percepto.co/',
        description: 'Autonomous drone-in-a-box inspections',
        hqCountry: 'IL',
      },
      {
        name: 'Fixar',
        url: 'https://fixar.pro/',
        description: 'Fixed-wing VTOL drones for survey & cargo',
        hqCountry: 'LV',
      },
      {
        name: 'Phoenix-Wings',
        url: 'https://phoenix-wings.com/',
        description: 'German cargo drone manufacturer',
        hqCountry: 'DE',
      },
      {
        name: 'ABZero',
        url: 'https://abzero.it/',
        description: 'Temperature-controlled drone organ transport',
        hqCountry: 'IT',
      },
    ],
  },
  {
    id: 'agriculture-equipment',
    label: 'Agriculture',
    icon: '\uD83C\uDF3E',
    entries: [
      {
        name: 'DJI (Agras)',
        url: 'https://ag.dji.com/',
        description: 'Agras series agricultural spraying drones',
        hqCountry: 'CN',
      },
      {
        name: 'XAG',
        url: 'https://www.xa.com/en',
        description: 'Autonomous agricultural drones and rovers',
        hqCountry: 'CN',
      },
      {
        name: 'Guardian Agriculture',
        url: 'https://guardian.ag/',
        description: 'eVTOL crop spraying platform (SC1)',
        hqCountry: 'US',
      },
      {
        name: 'Hylio',
        url: 'https://www.hyl.io/',
        description: 'American-made ag drone swarm spraying',
        hqCountry: 'US',
      },
      {
        name: 'AgEagle (EagleNXT)',
        url: 'https://eaglenxt.com/',
        description: 'Drones, sensors, and software for agriculture',
        hqCountry: 'US',
      },
      {
        name: 'Rantizo',
        url: 'https://rantizo.com/',
        description: 'Precision drone spraying for crop protection',
        hqCountry: 'US',
      },
      {
        name: 'Pyka',
        url: 'https://www.flypyka.com/',
        description: 'Autonomous electric crop-spraying aircraft',
        hqCountry: 'US',
      },
    ],
  },
  {
    id: 'mapping-surveying',
    label: 'Mapping & Surveying',
    icon: '\uD83D\uDDFA\uFE0F',
    entries: [
      {
        name: 'Skymap Africa',
        url: 'https://skymap-geo.com/',
        description: 'Drone surveys, LiDAR mapping, 3D modelling, and GIS services in East Africa',
        hqCountry: 'KE',
      },
      {
        name: 'Wingtra',
        url: 'https://wingtra.com/',
        description: 'VTOL mapping drones from Zurich',
        hqCountry: 'CH',
      },
      {
        name: 'Delair',
        url: 'https://delair.aero/',
        description: 'Fixed-wing UAVs and analytics from Toulouse',
        hqCountry: 'FR',
      },
      {
        name: 'senseFly (EagleNXT)',
        url: 'https://eaglenxt.com/drones/ebee-x/',
        description: 'eBee fixed-wing mapping drones',
        hqCountry: 'CH',
      },
      {
        name: 'JOUAV',
        url: 'https://www.jouav.com/',
        description: 'VTOL industrial drones from China',
        hqCountry: 'CN',
      },
      {
        name: 'Quantum-Systems',
        url: 'https://quantum-systems.com/',
        description: 'Trinity and Vector VTOL mapping drones',
        hqCountry: 'DE',
      },
    ],
  },
  {
    id: 'cargo-delivery',
    label: 'Cargo & Delivery',
    icon: '\uD83D\uDCE6',
    entries: [
      {
        name: 'Micromek',
        url: 'https://www.micromek.net/',
        description: 'EcoSoar UAV \u2014 low-cost medical delivery drone built in Malawi',
        hqCountry: 'MW',
      },
      {
        name: 'Zipline',
        url: 'https://www.flyzipline.com/',
        description: 'Autonomous medical and commercial delivery',
        hqCountry: 'US',
      },
      {
        name: 'Wing (Alphabet)',
        url: 'https://wing.com/',
        description: 'Drone delivery service by Alphabet',
        hqCountry: 'US',
      },
      {
        name: 'Wingcopter',
        url: 'https://wingcopter.com/',
        description: 'Fixed-wing VTOL delivery drones from Germany',
        hqCountry: 'DE',
      },
      {
        name: 'Matternet',
        url: 'https://www.matternet.com/',
        description: 'Urban drone delivery and logistics',
        hqCountry: 'US',
      },
      {
        name: 'RigiTech',
        url: 'https://rigi.tech/',
        description: 'Long-range VTOL medical delivery drones',
        hqCountry: 'CH',
      },
      {
        name: 'Dronamics',
        url: 'https://www.dronamics.com/',
        description: 'Black Swan long-range cargo drone airline',
        hqCountry: 'BG',
      },
      {
        name: 'Swoop Aero',
        url: 'https://swoop.aero/',
        description: 'Medical drone delivery in remote regions',
        hqCountry: 'AU',
      },
      {
        name: 'Drone Delivery Canada',
        url: 'https://dronedeliverycanada.com/',
        description: 'Condor & Sparrow cargo drone platforms',
        hqCountry: 'CA',
      },
      {
        name: 'Manna Drone Delivery',
        url: 'https://www.manna.aero/',
        description: 'Last-mile residential drone delivery',
        hqCountry: 'IE',
      },
      {
        name: 'Windracers',
        url: 'https://windracers.org/',
        description: 'Large fixed-wing drones for remote logistics',
        hqCountry: 'GB',
      },
      {
        name: 'Speedbird Aero',
        url: 'https://speedbird.aero/',
        description: 'Brazilian drone delivery and logistics',
        hqCountry: 'BR',
      },
    ],
  },
  {
    id: 'components-accessories',
    label: 'Components & Accessories',
    icon: '\uD83D\uDD27',
    entries: [
      // ── African first ──
      {
        name: 'Flying Robot',
        url: 'https://flyingrobot.co/',
        description: 'FPV and commercial drone parts, Cape Town',
        hqCountry: 'ZA',
      },
      {
        name: 'GoUAV',
        url: 'https://gouav.co.za/',
        description: 'Authorized DJI Enterprise distributor, SA',
        hqCountry: 'ZA',
      },
      {
        name: 'PACSys',
        url: 'https://pacsys.co.za/',
        description: 'Africa\'s largest DJI agriculture drone dealer',
        hqCountry: 'ZA',
      },
      {
        name: 'StriekAir',
        url: 'https://www.striekair.com/',
        description: 'Drone equipment and aerial solutions',
        hqCountry: 'ZA',
      },
      // ── Global ──
      {
        name: 'MicaSense (EagleNXT)',
        url: 'https://eaglenxt.com/solutions/micasense-series-multispectral-cameras/',
        description: 'Multispectral drone sensors for agriculture',
        hqCountry: 'US',
      },
      {
        name: 'FLIR / Teledyne',
        url: 'https://www.flir.com/instruments/drone/',
        description: 'Thermal and infrared imaging for drones',
        hqCountry: 'US',
      },
      {
        name: 'T-Motor',
        url: 'https://uav-en.tmotor.com/',
        description: 'Drone motors, ESCs, and propellers',
        hqCountry: 'CN',
      },
      {
        name: 'Tattu / Gens Ace',
        url: 'https://genstattu.com/',
        description: 'High-performance LiPo batteries for UAVs',
        hqCountry: 'CN',
      },
      {
        name: 'Elsight (Halo)',
        url: 'https://elsight.com/',
        description: 'Multi-network drone connectivity platform',
        hqCountry: 'IL',
      },
    ],
  },
];

// ============================================================================
// 3. MANUFACTURER SECTIONS (Drones-for-Good / Use-Case Operators)
//    All entities from the "400 Feet" Drones for Good guide.
//    Expanded to 11 sections covering every use case.
// ============================================================================

export const MANUFACTURER_SECTIONS: ResourceSection[] = [
  {
    id: 'medical-delivery',
    label: 'Medical Delivery & Service Providers',
    icon: '\uD83C\uDFE5',
    entries: [
      // ── African first ──
      {
        name: 'LifeBank',
        url: 'https://lifebank.ng/',
        description: 'Blood and medical supply drone delivery',
        hqCountry: 'NG',
      },
      {
        name: 'Continental Drones',
        url: 'https://continentaldrones.com/',
        description: 'Pan-African Wingcopter partner, 49 countries',
        hqCountry: 'GH',
      },
      {
        name: 'Cloudline',
        url: 'https://www.cloudline.africa/',
        description: 'Solar-powered airships for medical delivery',
        hqCountry: 'ZA',
      },
      // ── Global ──
      {
        name: 'Zipline',
        url: 'https://www.flyzipline.com/',
        description: 'Medical delivery in Rwanda, Ghana, Kenya, Nigeria',
        hqCountry: 'US',
      },
      {
        name: 'Wingcopter',
        url: 'https://wingcopter.com/',
        description: 'Fixed-wing VTOL delivery drones from Germany',
        hqCountry: 'DE',
      },
      {
        name: 'Swoop Aero',
        url: 'https://swoop.aero/',
        description: 'Medical drone delivery in Malawi, DRC, Mozambique',
        hqCountry: 'AU',
      },
      {
        name: 'Matternet',
        url: 'https://www.matternet.com/',
        description: 'Urban drone delivery and logistics',
        hqCountry: 'US',
      },
      {
        name: 'Apian',
        url: 'https://www.apian.aero/',
        description: 'NHS drone delivery for UK healthcare',
        hqCountry: 'GB',
      },
      {
        name: 'Skyports Drone Services',
        url: 'https://skyports.net/',
        description: 'Advanced air mobility and drone delivery',
        hqCountry: 'GB',
      },
      {
        name: 'DroneUp',
        url: 'https://www.droneup.com/',
        description: 'Drone delivery for retail and healthcare',
        hqCountry: 'US',
      },
      {
        name: 'Skye Air',
        url: 'https://skyeair.tech/',
        description: 'Indian autonomous drone delivery platform',
        hqCountry: 'IN',
      },
      {
        name: 'Medical Drone Service',
        url: 'https://www.medicaldroneservice.com/',
        description: 'German medical specimen transport drones',
        hqCountry: 'DE',
      },
      {
        name: 'Prokura',
        url: 'https://prokurainnovations.com/',
        description: 'Drone innovation for healthcare delivery',
        hqCountry: 'IN',
      },
      {
        name: 'Delivrone',
        url: 'https://delivrone.com/',
        description: 'Drone delivery service provider',
        hqCountry: 'TR',
      },
      {
        name: 'TechEagle',
        url: 'https://www.techeagle.in/',
        description: 'Indian BVLOS drone delivery for healthcare',
        hqCountry: 'IN',
      },
      {
        name: 'AerialMetric',
        url: 'https://aerialmetric.com/',
        description: 'Drone delivery solutions in Bangladesh',
        hqCountry: 'BD',
      },
      {
        name: 'Avy',
        url: 'https://avy.eu/',
        description: 'VTOL drones for emergency medical delivery',
        hqCountry: 'NL',
      },
      {
        name: 'Jedsy',
        url: 'https://jedsy.com/',
        description: 'Autonomous drone delivery from Switzerland',
        hqCountry: 'CH',
      },
      {
        name: 'Redwing Labs',
        url: 'https://www.redwinglabs.com/',
        description: 'Indian drone delivery for rural healthcare',
        hqCountry: 'IN',
      },
      {
        name: 'ABZero',
        url: 'https://abzero.it/',
        description: 'Temperature-controlled drone organ transport',
        hqCountry: 'IT',
      },
      {
        name: 'Manna Drone Delivery',
        url: 'https://www.manna.aero/',
        description: 'Last-mile residential drone delivery',
        hqCountry: 'IE',
      },
      {
        name: 'United Nations Population Fund (UNFPA)',
        url: 'https://www.unfpa.org/',
        description: 'Medical drone delivery for maternal health supplies',
        hqCountry: 'US',
      },
    ],
  },
  {
    id: 'humanitarian-response',
    label: 'Humanitarian & Disaster Response',
    icon: '\uD83C\uDF0D',
    entries: [
      // ── African first ──
      {
        name: 'South Africa Flying Labs',
        url: 'https://flyinglabs.org/southafrica',
        description: 'Drone innovation and collaboration in SA',
        hqCountry: 'ZA',
      },
      {
        name: 'Kenya Flying Labs',
        url: 'https://flyinglabs.org/kenya',
        description: 'Humanitarian drone solutions in Kenya',
        hqCountry: 'KE',
      },
      // ── Global ──
      {
        name: 'WeRobotics',
        url: 'https://werobotics.org/',
        description: 'Flying Labs network for social good',
        hqCountry: 'CH',
      },
      {
        name: 'UNICEF Drones',
        url: 'https://www.unicef.org/innovation/drones',
        description: 'Drone corridors and delivery for children',
        hqCountry: 'US',
      },
      {
        name: 'VillageReach',
        url: 'https://villagereach.org/',
        description: 'Health supply chain innovation in Africa',
        hqCountry: 'US',
      },
      {
        name: 'World Food Programme Drones',
        url: 'https://innovation.wfp.org/project/drones',
        description: 'WFP drone operations for food security',
        hqCountry: 'IT',
      },
      {
        name: 'Humanitarian UAV Network',
        url: 'https://uavhumanitariannetwork.org/',
        description: 'Coordination hub for humanitarian UAV ops',
      },
      {
        name: 'International Committee of the Red Cross',
        url: 'https://www.icrc.org/',
        description: 'Humanitarian aid and drone innovation',
        hqCountry: 'CH',
      },
      {
        name: 'Drones Without Borders',
        url: 'https://droneswithoutborders.org/',
        description: 'Drone tech for humanitarian and social causes',
      },
      {
        name: 'Airborne International Response Team',
        url: 'https://aaborneresponse.org/',
        description: 'AIRT disaster response UAS coordination',
        hqCountry: 'US',
      },
      {
        name: 'GlobalMedic (RescUAV)',
        url: 'https://globalmedic.ca/',
        description: 'Disaster response drones and medical relief',
        hqCountry: 'CA',
      },
      {
        name: 'Help.NGO',
        url: 'https://help.ngo/',
        description: 'Humanitarian tech and emergency drones',
        hqCountry: 'US',
      },
      {
        name: 'NAXA',
        url: 'https://naxa.com.np/',
        description: 'Drone mapping for development in Nepal',
        hqCountry: 'NP',
      },
      {
        name: 'QP Drone Tech',
        url: 'https://qpgroup.com.np/',
        description: 'Drone technology for Nepal development',
        hqCountry: 'NP',
      },
      {
        name: 'Nepal Flying Labs',
        url: 'https://flyinglabs.org/nepal',
        description: 'Humanitarian drones and disaster mapping',
        hqCountry: 'NP',
      },
      {
        name: 'UNFPA',
        url: 'https://www.unfpa.org/',
        description: 'UN Population Fund drone health initiatives',
        hqCountry: 'US',
      },
      {
        name: 'PSI (Population Services International)',
        url: 'https://www.psi.org/',
        description: 'Health organization using drone delivery',
        hqCountry: 'US',
      },
      {
        name: 'World Bank',
        url: 'https://www.worldbank.org/',
        description: 'Development funding for drone infrastructure',
        hqCountry: 'US',
      },
      {
        name: 'World Economic Forum',
        url: 'https://www.weforum.org/',
        description: 'Drone delivery regulatory and policy advocacy',
        hqCountry: 'CH',
      },
      {
        name: 'Humanitarian OpenStreetMap',
        url: 'https://www.hotosm.org/',
        description: 'Open mapping for humanitarian response',
        hqCountry: 'US',
      },
      {
        name: 'Esri',
        url: 'https://www.esri.com/',
        description: 'GIS platform for humanitarian drone mapping',
        hqCountry: 'US',
      },
      {
        name: 'International Emergency Drone Organization',
        url: 'https://www.iedo-drone.org/',
        description: 'Coordinates volunteer drone teams for emergency response worldwide',
        hqCountry: 'FR',
      },
    ],
  },
  {
    id: 'reforestation',
    label: 'Reforestation & Tree Planting',
    icon: '\uD83C\uDF33',
    entries: [
      // ── African first ──
      {
        name: 'AeroSeed Africa',
        url: 'https://www.aeroseedafrica.com/',
        description: 'Aerial seeding for reforestation in Africa',
        hqCountry: 'ZA',
      },
      // ── Global ──
      {
        name: 'Flash Forest',
        url: 'https://flashforest.ca/',
        description: 'Drone reforestation with seed pods',
        hqCountry: 'CA',
      },
      {
        name: 'Dendra Systems',
        url: 'https://www.dendra.io/',
        description: 'AI-powered drone ecosystem restoration',
        hqCountry: 'GB',
      },
      {
        name: 'Mast Reforestation',
        url: 'https://www.mastreforest.com/',
        description: 'Drone-based reforestation after wildfires',
        hqCountry: 'US',
      },
      {
        name: 'AirSeed Technologies',
        url: 'https://airseedtech.com/',
        description: 'Drone seed dispersal pods for restoration',
        hqCountry: 'AU',
      },
      {
        name: 'Seedcopter',
        url: 'https://seedcopter.com/',
        description: 'Drone-based seed planting technology',
        hqCountry: 'AU',
      },
      {
        name: 'Dronecoria',
        url: 'https://dronecoria.org/',
        description: 'Open-source drone reforestation from Spain',
        hqCountry: 'ES',
      },
      {
        name: 'Skyseed',
        url: 'https://www.skyseed.green/',
        description: 'German drone seeding for ecosystem recovery',
        hqCountry: 'DE',
      },
      {
        name: 'Treeswift',
        url: 'https://treeswift.com/',
        description: 'Autonomous forest inventory and monitoring',
        hqCountry: 'US',
      },
      {
        name: 'CO2 Revolution',
        url: 'https://co2revolution.es/',
        description: 'Smart seed drone planting from Spain',
        hqCountry: 'ES',
      },
      {
        name: 'Morfo',
        url: 'https://www.morfo.rest/',
        description: 'French drone reforestation at scale',
        hqCountry: 'FR',
      },
      {
        name: 'Air Forestry',
        url: 'https://www.airforestry.com/',
        description: 'Swedish drone forestry management',
        hqCountry: 'SE',
      },
      {
        name: 'Bioverse Labs',
        url: 'https://www.bioverselabs.com/',
        description: 'Brazilian biodiversity drone restoration',
        hqCountry: 'BR',
      },
      {
        name: 'Lord of the Trees',
        url: 'https://lordofthetrees.org/',
        description: 'Drone-based tree planting initiative',
      },
      {
        name: 'Ecording',
        url: 'https://ecording.com/',
        description: 'Recordable seed balls for drone dispersal',
        hqCountry: 'DE',
      },
      {
        name: 'Inverto',
        url: 'https://inverto.earth/',
        description: 'Drone reforestation technology',
      },
      {
        name: 'Distant Imagery',
        url: 'https://www.distantimagery.com/',
        description: 'Aerial imagery for forestry and conservation',
        hqCountry: 'AU',
      },
      {
        name: 'Panama Flying Labs',
        url: 'https://flyinglabs.org/panama',
        description: 'Drone conservation and reforestation in Panama',
        hqCountry: 'PA',
      },
    ],
  },
  {
    id: 'wildlife-marine',
    label: 'Wildlife & Marine Conservation',
    icon: '\uD83D\uDC0B',
    entries: [
      // ── Conservation ──
      {
        name: 'Air Shepherd',
        url: 'https://www.airshepherd.org/',
        description: 'Drone anti-poaching patrols in southern Africa',
        hqCountry: 'US',
      },
      {
        name: 'Conservation Drones',
        url: 'https://conservationdrones.org/',
        description: 'Low-cost drones for wildlife conservation',
        hqCountry: 'AU',
      },
      {
        name: 'Wildlife Drones',
        url: 'https://wildlifedrones.net/',
        description: 'Drone wildlife tracking and monitoring',
        hqCountry: 'AU',
      },
      {
        name: 'WildDrone',
        url: 'https://wilddrone.eu/',
        description: 'EU project for drone biodiversity monitoring',
        hqCountry: 'DK',
      },
      {
        name: 'Conservation AI',
        url: 'https://conservationai.co.uk/',
        description: 'AI species detection from drone imagery',
        hqCountry: 'GB',
      },
      {
        name: 'WildTrack',
        url: 'https://wildtrack.org/',
        description: 'Non-invasive wildlife footprint identification',
        hqCountry: 'GB',
      },
      {
        name: 'Envico Technologies',
        url: 'https://envicotechnologies.com/',
        description: 'Environmental drone monitoring and surveys',
        hqCountry: 'GB',
      },
      // ── Marine ──
      {
        name: 'Ocean Alliance',
        url: 'https://whale.org/',
        description: 'SnotBot drone whale research program',
        hqCountry: 'US',
      },
      {
        name: 'Saildrone',
        url: 'https://www.saildrone.com/',
        description: 'Autonomous ocean vessels for research',
        hqCountry: 'US',
      },
      {
        name: 'XOCEAN',
        url: 'https://xocean.com/',
        description: 'Uncrewed surface vessels for ocean data',
        hqCountry: 'IE',
      },
      {
        name: 'Kraken Robotics',
        url: 'https://krakenrobotics.com/',
        description: 'Subsea robotics and sonar for ocean survey',
        hqCountry: 'CA',
      },
      {
        name: 'Advanced Navigation',
        url: 'https://www.advancednavigation.com/',
        description: 'Marine and aerial navigation systems',
        hqCountry: 'AU',
      },
      {
        name: 'Ocean Infinity',
        url: 'https://oceaninfinity.com/',
        description: 'Robotic vessels for ocean exploration',
        hqCountry: 'GB',
      },
      {
        name: 'Liquid Robotics',
        url: 'https://www.liquid-robotics.com/',
        description: 'Wave Glider autonomous ocean robots',
        hqCountry: 'US',
      },
      {
        name: 'Bedrock',
        url: 'https://bedrockoceansolutions.com/',
        description: 'Autonomous subsea mapping platform',
        hqCountry: 'US',
      },
      {
        name: 'TerraDepth',
        url: 'https://terradepth.com/',
        description: 'Autonomous ocean data acquisition',
        hqCountry: 'US',
      },
      {
        name: 'ecoSub Robotics',
        url: 'https://ecosub.co.uk/',
        description: 'Low-cost autonomous underwater vehicles',
        hqCountry: 'GB',
      },
      {
        name: 'Eelume',
        url: 'https://eelume.com/',
        description: 'Snake-like subsea robots from Norway',
        hqCountry: 'NO',
      },
      {
        name: 'Neptune Robotics',
        url: 'https://neptonics.com/',
        description: 'Marine autonomous inspection robots',
        hqCountry: 'CA',
      },
      {
        name: 'Drones for Whale Research',
        url: 'https://dronesforwhaleresearch.com/',
        description: 'Cetacean research with UAV technology',
        hqCountry: 'US',
      },
      {
        name: 'Ceto Marine Research',
        url: 'https://cetomarineresearch.com/',
        description: 'Marine mammal drone surveys',
        hqCountry: 'GB',
      },
      {
        name: 'CitizenScienceGIS',
        url: 'https://www.citizensciencegis.org/',
        description: 'Community drone mapping for marine science',
        hqCountry: 'US',
      },
      {
        name: 'MarSIS',
        url: 'https://marsis-project.eu/',
        description: 'Marine drone surveillance and information',
        hqCountry: 'EU',
      },
      {
        name: 'TellUs4D',
        url: 'https://tellus4d.com/',
        description: 'Drone and satellite environmental monitoring',
        hqCountry: 'US',
      },
      {
        name: 'SeaDrone',
        url: 'https://seadrone.eu/',
        description: 'Autonomous surface and underwater drones',
        hqCountry: 'EU',
      },
    ],
  },
  {
    id: 'agriculture-operators',
    label: 'Agriculture & Food Systems',
    icon: '\uD83C\uDF31',
    entries: [
      // ── African first ──
      {
        name: 'Aerobotics',
        url: 'https://aerobotics.com/',
        description: 'AI-powered drone analytics for fruit farmers',
        hqCountry: 'ZA',
      },
      {
        name: 'SOWIT',
        url: 'https://en.sowit.fr/',
        description: 'Satellite and drone precision agriculture',
        hqCountry: 'MA',
      },
      {
        name: 'Agrihawk',
        url: 'https://www.agrihawk.co.za/',
        description: 'Agricultural drone services and DJI dealer',
        hqCountry: 'ZA',
      },
      {
        name: 'ProWings Africa',
        url: 'https://prowingsafrica.com/',
        description: 'Drone training and agriculture services',
        hqCountry: 'ZA',
      },
      {
        name: 'Equinox\'s Drones',
        url: 'https://equinoxsdrones.com/',
        description: 'Egyptian agricultural and energy drone services',
        hqCountry: 'EG',
      },
      // ── Global ──
      {
        name: 'Sentera',
        url: 'https://sentera.com/',
        description: 'Precision agriculture drones and sensors',
        hqCountry: 'US',
      },
      {
        name: 'Taranis',
        url: 'https://taranis.com/',
        description: 'AI crop intelligence platform',
        hqCountry: 'IL',
      },
      {
        name: 'Tevel Aerobotics',
        url: 'https://www.tevel-tech.com/',
        description: 'Autonomous fruit-picking flying robots',
        hqCountry: 'IL',
      },
      {
        name: 'PrecisionHawk',
        url: 'https://www.precisionhawk.com/',
        description: 'Drone data analytics for enterprise',
        hqCountry: 'US',
      },
      {
        name: 'Aonic',
        url: 'https://aonic.co/',
        description: 'Malaysian drone ag services and spraying',
        hqCountry: 'MY',
      },
      {
        name: 'Deveron',
        url: 'https://deveron.com/',
        description: 'Canadian drone agriculture data analytics',
        hqCountry: 'CA',
      },
      {
        name: 'Rocketfarm',
        url: 'https://rocketfarm.no/',
        description: 'Norwegian autonomous agricultural drones',
        hqCountry: 'NO',
      },
      {
        name: 'Agremo',
        url: 'https://www.agremo.com/',
        description: 'AI-powered drone data analysis for agriculture',
        hqCountry: 'RS',
      },
      {
        name: 'ScholarFarms',
        url: 'https://scholarfarms.com/',
        description: 'AI-driven drone farm management',
        hqCountry: 'US',
      },
      {
        name: 'Raven Industries',
        url: 'https://ravenind.com/',
        description: 'Precision agriculture technology (CNH)',
        hqCountry: 'US',
      },
      {
        name: 'Farm Flight',
        url: 'https://farmflight.co.uk/',
        description: 'Agricultural drone surveys and mapping',
        hqCountry: 'GB',
      },
      {
        name: 'Aksata',
        url: 'https://aksata.nl/',
        description: 'Dutch drone crop monitoring and analytics',
        hqCountry: 'NL',
      },
      {
        name: 'Investiv',
        url: 'https://investiv.co/',
        description: 'Drone agriculture technology solutions',
        hqCountry: 'US',
      },
      {
        name: 'Stablegen',
        url: 'https://stablegen.com/',
        description: 'Livestock monitoring drone technology',
        hqCountry: 'US',
      },
      {
        name: 'Integrated Aerial Precision',
        url: 'https://www.iaprecision.com/',
        description: 'Precision agriculture drone services in Nigeria',
        hqCountry: 'NG',
      },
      {
        name: 'Megadrone Brazil',
        url: 'https://megadronebrasil.com.br/',
        description: 'Agricultural drone spraying services',
        hqCountry: 'BR',
      },
      {
        name: 'Agrinfo',
        url: 'https://www.agrinfo.co.tz/',
        description: 'Agricultural drone data platform in Tanzania',
        hqCountry: 'TZ',
      },
    ],
  },
  {
    id: 'emergency-wildfire-sar',
    label: 'Emergency, Wildfire & SAR',
    icon: '\uD83D\uDE92',
    entries: [
      {
        name: 'DRONERESPONDERS',
        url: 'https://www.droneresponders.org/',
        description: 'Public safety drone community and resources',
        hqCountry: 'US',
      },
      {
        name: 'Robotto',
        url: 'https://robotto.ai/',
        description: 'AI drones for wildfire and SAR missions',
        hqCountry: 'DK',
      },
      {
        name: 'IGNIS (Drone Amplified)',
        url: 'https://droneamplified.com/',
        description: 'Precision aerial ignition for fire management',
        hqCountry: 'US',
      },
      {
        name: 'Rain',
        url: 'https://rain.aero/',
        description: 'Autonomous firefighting drone swarms',
        hqCountry: 'US',
      },
      {
        name: 'Overwatch Aero',
        url: 'https://overwatchaero.com/',
        description: 'Aerial wildfire monitoring and response',
        hqCountry: 'US',
      },
      {
        name: 'SkyBound Rescuer',
        url: 'https://skyboundrescuer.com/',
        description: 'Drone platform for search and rescue ops',
        hqCountry: 'US',
      },
      {
        name: 'Drone SAR Chile',
        url: 'https://dronesarchile.cl/',
        description: 'Chilean drone search and rescue network',
        hqCountry: 'CL',
      },
      {
        name: 'National Fire Protection Association',
        url: 'https://www.nfpa.org/',
        description: 'Fire safety standards and drone guidance',
        hqCountry: 'US',
      },
      {
        name: 'Avy',
        url: 'https://avy.eu/',
        description: 'VTOL drones for emergency & fire response',
        hqCountry: 'NL',
      },
      {
        name: 'FireMan Consortium',
        url: 'https://fireman-project.eu/',
        description: 'EU research project on drone swarms for wildfire monitoring',
        hqCountry: 'FI',
      },
    ],
  },
  {
    id: 'public-health-disease',
    label: 'Public Health & Disease Control',
    icon: '\uD83E\uDDA0',
    entries: [
      {
        name: 'World Mosquito Program',
        url: 'https://www.worldmosquitoprogram.org/',
        description: 'Wolbachia mosquito drone release program',
        hqCountry: 'AU',
      },
      {
        name: 'Anti-Malaria Drones',
        url: 'https://www.antimalariadrones.com/',
        description: 'Drone-based malaria vector control',
        hqCountry: 'TZ',
      },
      {
        name: 'Birdview',
        url: 'https://www.birdview.technology/',
        description: 'Drone sterile insect release for disease control',
        hqCountry: 'DE',
      },
      {
        name: 'Liverpool School of Tropical Medicine',
        url: 'https://www.lstmed.ac.uk/',
        description: 'Drone research for tropical disease control',
        hqCountry: 'GB',
      },
      {
        name: 'Moscamed Brasil',
        url: 'https://moscamed.org.br/',
        description: 'Brazilian sterile insect drone programme',
        hqCountry: 'BR',
      },
      {
        name: 'Indian Council of Medical Research',
        url: 'https://www.icmr.gov.in/',
        description: 'Medical research including drone health tech',
        hqCountry: 'IN',
      },
      {
        name: 'Vector Disease Control International',
        url: 'https://www.vdci.net/',
        description: 'Mosquito control with aerial drone spraying',
        hqCountry: 'US',
      },
      {
        name: 'Orange County Mosquito & Vector Control District',
        url: 'https://www.ocvector.org/',
        description: 'US mosquito control district using drone spraying',
        hqCountry: 'US',
      },
      {
        name: 'Metropolitan Mosquito Control District',
        url: 'https://mmcd.org/',
        description: 'US mosquito control district with drone applications',
        hqCountry: 'US',
      },
    ],
  },
  {
    id: 'energy-climate-infrastructure',
    label: 'Energy, Climate & Infrastructure',
    icon: '\u26A1',
    entries: [
      // ── African first ──
      {
        name: 'Rocketmine',
        url: 'https://www.rocketmine.com/',
        description: 'First SA commercial drone operator, mining surveys',
        hqCountry: 'ZA',
      },
      {
        name: 'TerraCam',
        url: 'https://www.terracam.co.za/',
        description: 'Africa\'s pioneer commercial drone surveying',
        hqCountry: 'ZA',
      },
      {
        name: 'Orbital Africa',
        url: 'https://orbital.co.ke/',
        description: 'Drone mapping across East Africa',
        hqCountry: 'KE',
      },
      // ── Global ──
      {
        name: 'Terra Drone',
        url: 'https://terra-drone.net/global/',
        description: 'World\'s largest industrial drone solutions',
        hqCountry: 'JP',
      },
      {
        name: 'Cyberhawk',
        url: 'https://thecyberhawk.com/',
        description: 'Drone inspection and iHawk data platform',
        hqCountry: 'GB',
      },
      {
        name: 'Sharper Shape',
        url: 'https://sharpershape.com/',
        description: 'Automated utility inspection digital twins',
        hqCountry: 'FI',
      },
      {
        name: 'GLOBHE',
        url: 'https://www.globhe.com/',
        description: 'Crowddroning platform connecting 11,000+ pilots',
        hqCountry: 'SE',
      },
      {
        name: 'Percepto',
        url: 'https://percepto.co/',
        description: 'Autonomous drone-in-a-box inspections',
        hqCountry: 'IL',
      },
      {
        name: 'SkySpecs',
        url: 'https://skyspecs.com/',
        description: 'Automated wind turbine blade inspection',
        hqCountry: 'US',
      },
      {
        name: 'Raptor Maps',
        url: 'https://raptormaps.com/',
        description: 'Solar farm drone inspection analytics',
        hqCountry: 'US',
      },
      {
        name: 'Zeitview',
        url: 'https://zeitview.com/',
        description: 'Aerial intelligence for infrastructure',
        hqCountry: 'US',
      },
      {
        name: 'Consortiq',
        url: 'https://consortiq.com/',
        description: 'Drone program management and LAANC',
        hqCountry: 'US',
      },
      {
        name: 'Skypull',
        url: 'https://skypull.com/',
        description: 'Drone-based airborne wind energy systems',
        hqCountry: 'CH',
      },
      {
        name: 'Aerialtronics',
        url: 'https://aerialtronics.com/',
        description: 'AI-powered industrial drone inspections',
        hqCountry: 'NL',
      },
      {
        name: 'KiteKraft',
        url: 'https://kitekraft.de/',
        description: 'Flying wind turbines for renewable energy',
        hqCountry: 'DE',
      },
      {
        name: 'EnerKite',
        url: 'https://enerkite.de/',
        description: 'Airborne wind energy kite systems',
        hqCountry: 'DE',
      },
      {
        name: 'qAIRa',
        url: 'https://www.qaira.com/',
        description: 'Air quality monitoring drones from Peru',
        hqCountry: 'PE',
      },
      {
        name: 'Aeromon',
        url: 'https://aeromon.io/',
        description: 'Industrial emission monitoring drones',
        hqCountry: 'FI',
      },
      {
        name: 'Meteomatics',
        url: 'https://www.meteomatics.com/',
        description: 'Weather drone data for atmospheric profiling',
        hqCountry: 'CH',
      },
      {
        name: 'Near Space Labs',
        url: 'https://nearspacelabs.com/',
        description: 'Stratospheric drone imagery platform',
        hqCountry: 'US',
      },
      {
        name: 'BotLink',
        url: 'https://botlink.com/',
        description: 'Commercial drone flight and data platform',
        hqCountry: 'US',
      },
      {
        name: 'Scentroid',
        url: 'https://scentroid.com/',
        description: 'Drone-mounted odour and gas monitoring',
        hqCountry: 'CA',
      },
      {
        name: 'R2Home',
        url: 'https://r2home.space/',
        description: 'French parafoil drone for atmospheric data',
        hqCountry: 'FR',
      },
      {
        name: '60Hertz Energy',
        url: 'https://60hertzenergy.com/',
        description: 'Solar panel drone inspection services',
        hqCountry: 'US',
      },
      {
        name: 'First Airborne',
        url: 'https://firstairborne.com/',
        description: 'Wind turbine drone inspection and performance testing',
        hqCountry: 'US',
      },
      {
        name: 'Elythor',
        url: 'https://elythor.com/',
        description: 'Drone energy infrastructure inspection services',
        hqCountry: 'CH',
      },
      {
        name: 'Kestrix',
        url: 'https://www.kestrix.io/',
        description: 'AI heat loss mapping drones for building energy audits',
        hqCountry: 'GB',
      },
      {
        name: 'Map&Rank',
        url: 'https://mapnrank.com/',
        description: 'Climate and environmental data mapping for communities',
        hqCountry: 'CM',
      },
      {
        name: 'Climate Robotics Network',
        url: 'https://climaterobotics.network/',
        description: 'Research network for climate robotics solutions',
        hqCountry: 'CH',
      },
      {
        name: 'Outreach Robotics',
        url: 'https://outreachrobotics.com/',
        description: 'Environmental and forestry drone sampling solutions',
        hqCountry: 'CA',
      },
    ],
  },
  {
    id: 'water-demining',
    label: 'Water, Sanitation & Demining',
    icon: '\uD83D\uDCA7',
    entries: [
      // ── African first ──
      {
        name: "C\u00F4te d'Ivoire Flying Labs",
        url: 'https://flyinglabs.org/cotedivoire',
        description: 'Drone demining and development in West Africa',
        hqCountry: 'CI',
      },
      {
        name: 'Malawi Red Cross Society',
        url: 'https://www.malawirc.org/',
        description: 'Drone flood mapping and WASH monitoring',
        hqCountry: 'MW',
      },
      // ── Global ──
      {
        name: 'Mine Kafon',
        url: 'https://minekafon.org/',
        description: 'Drone-based landmine detection and clearing',
        hqCountry: 'NL',
      },
      {
        name: 'Swiss Foundation for Mine Action',
        url: 'https://www.fsd.ch/',
        description: 'Humanitarian drone demining programmes',
        hqCountry: 'CH',
      },
      {
        name: 'Humanity & Inclusion',
        url: 'https://www.hi.org/',
        description: 'Disability-focused demining with drone tech',
        hqCountry: 'FR',
      },
      {
        name: 'Mobility Robotics',
        url: 'https://mobilityrobotics.com/',
        description: 'Autonomous mine detection robots',
        hqCountry: 'US',
      },
      {
        name: 'Fluid Robotics',
        url: 'https://fluidrobotics.com/',
        description: 'Autonomous water infrastructure drones',
        hqCountry: 'IN',
      },
      {
        name: 'Pipebots',
        url: 'https://pipebots.ac.uk/',
        description: 'Micro-robots for water pipe inspection',
        hqCountry: 'GB',
      },
      {
        name: 'WADI',
        url: 'https://wadi-project.eu/',
        description: 'Drone-based water leakage detection',
        hqCountry: 'EU',
      },
      {
        name: 'Water for Good',
        url: 'https://waterforgood.org/',
        description: 'Drone mapping for water supply in CAR',
        hqCountry: 'US',
      },
      {
        name: 'Smart Terra',
        url: 'https://smartterra.co/',
        description: 'Drone monitoring for water infrastructure',
        hqCountry: 'GB',
      },
      {
        name: 'Demining Research Community',
        url: 'https://de-mine.com/',
        description: 'Research community for drone-based humanitarian demining',
        hqCountry: 'US',
      },
    ],
  },
  {
    id: 'informal-settlements',
    label: 'Informal Settlements & Urban',
    icon: '\uD83C\uDFD8\uFE0F',
    entries: [
      // ── African first ──
      {
        name: 'Code for Africa',
        url: 'https://codeforafrica.org/',
        description: 'Open data and drone mapping across Africa',
        hqCountry: 'KE',
      },
      {
        name: 'Kenya Red Cross Society',
        url: 'https://www.redcross.or.ke/',
        description: 'Drone mapping for disaster and development',
        hqCountry: 'KE',
      },
      // ── Global ──
      {
        name: 'UNHCR',
        url: 'https://www.unhcr.org/',
        description: 'Refugee camp drone mapping and monitoring',
        hqCountry: 'CH',
      },
      {
        name: 'IOM (International Organization for Migration)',
        url: 'https://www.iom.int/',
        description: 'Drone tech for migration and displacement',
        hqCountry: 'CH',
      },
      {
        name: 'Techo',
        url: 'https://www.techo.org/',
        description: 'Latin American informal settlement drone mapping',
        hqCountry: 'CL',
      },
    ],
  },
  {
    id: 'community-nonprofits',
    label: 'Community & Non-Profits',
    icon: '\uD83E\uDD1D',
    entries: [
      // ── African first ──
      {
        name: 'africanDRONE',
        url: 'https://africandrone.org/',
        description: 'African drone community and academy',
        hqCountry: 'ZA',
      },
      {
        name: 'African Drone Forum',
        url: 'https://www.africandroneforum.org/',
        description: 'Africa\'s leading drone policy and innovation platform',
        hqCountry: 'RW',
      },
      {
        name: 'Kenya Flying Labs',
        url: 'https://flyinglabs.org/kenya',
        description: 'Humanitarian drone solutions in Kenya',
        hqCountry: 'KE',
      },
      {
        name: 'Tanzania Flying Labs',
        url: 'https://flyinglabs.org/tanzania',
        description: 'First African Flying Labs, drone incubation',
        hqCountry: 'TZ',
      },
      {
        name: 'South Africa Flying Labs',
        url: 'https://flyinglabs.org/southafrica',
        description: 'Drone innovation and collaboration in SA',
        hqCountry: 'ZA',
      },
      {
        name: 'Ghana Flying Labs',
        url: 'https://flyinglabs.org/ghana',
        description: 'Agriculture and mapping drones in Ghana',
        hqCountry: 'GH',
      },
      // ── Global ──
      {
        name: 'WeRobotics / Flying Labs',
        url: 'https://werobotics.org/',
        description: 'Flying Labs network in 30+ countries',
        hqCountry: 'CH',
      },
      {
        name: 'Four Hundred Feet',
        url: 'https://fourhundredfeet.com/',
        description: 'Drones-for-good consultancy, Nairobi',
        hqCountry: 'KE',
      },
      {
        name: 'Airwards',
        url: 'https://airwards.com/',
        description: 'Awards recognising drone technology for good',
        hqCountry: 'GB',
      },
      {
        name: 'Girls Fly Africa',
        url: 'https://girlsflyafrica.org',
        description: 'Empowering African girls and women in aviation, aerospace, and drones',
        hqCountry: 'ZA',
      },
      {
        name: 'Women and Drones Africa',
        url: 'https://womenanddrones.com/wd-africa/',
        description: 'Africa chapter connecting female drone professionals across the continent',
        hqCountry: 'ZA',
      },
      {
        name: 'Women and Drones',
        url: 'https://womenanddrones.com/',
        description: 'Global community for women in drones',
        hqCountry: 'US',
      },
      {
        name: 'Black Girls Drone World',
        url: 'https://www.blackgirlsdroneworld.com/',
        description: 'Empowering Black women in drone technology',
        hqCountry: 'US',
      },
      {
        name: 'Drones Doing Good Alliance',
        url: 'https://dronesdoinggood.com/',
        description: 'Alliance for positive drone applications',
      },
      {
        name: 'Flying Labs Network',
        url: 'https://flyinglabs.org/',
        description: 'Global network of locally-led drone labs',
        hqCountry: 'CH',
      },
      {
        name: 'Nepal Flying Labs',
        url: 'https://flyinglabs.org/nepal',
        description: 'Humanitarian drones and disaster mapping',
        hqCountry: 'NP',
      },
    ],
  },
];

// ============================================================================
// 4. SOFTWARE SECTIONS
// ============================================================================

export const SOFTWARE_SECTIONS: ResourceSection[] = [
  {
    id: 'open-mapping-stack',
    label: 'Open Mapping Stack',
    icon: '\uD83D\uDD17',
    entries: [
      {
        name: '1. Task — HOT Drone Tasking Manager',
        url: 'https://drone.hotosm.org/',
        description: 'Coordinate pilots, assign flight areas, plan collaborative mapping missions. Free & open source.',
      },
      {
        name: '2. Process — OpenDroneMap',
        url: 'https://www.opendronemap.org/',
        description: 'Turn your aerial photos into orthomosaics, 3D models, and elevation maps. Free & open source.',
      },
      {
        name: '3. Host — OpenAerialMap',
        url: 'https://openaerialmap.org/',
        description: 'Publish your processed imagery openly for anyone to use. Free, CC-BY licensed commons.',
      },
    ],
  },
  {
    id: 'mapping-software',
    label: 'Mapping & Photogrammetry',
    icon: '\uD83D\uDDFA\uFE0F',
    entries: [
      {
        name: '1. Task — HOT Drone Tasking Manager',
        url: 'https://drone.hotosm.org/',
        description: 'Coordinate pilots, assign flight areas, plan collaborative mapping missions. Free & open source.',
      },
      {
        name: '2. Process — OpenDroneMap',
        url: 'https://www.opendronemap.org/',
        description: 'Turn your aerial photos into orthomosaics, 3D models, and elevation maps. Free & open source.',
      },
      {
        name: '3. Host — OpenAerialMap',
        url: 'https://openaerialmap.org/',
        description: 'Publish your processed imagery openly for anyone to use. Free, CC-BY licensed commons.',
      },
      {
        name: 'DroneDeploy',
        url: 'https://www.dronedeploy.com/',
        description: 'Cloud-based drone mapping and analytics',
        hqCountry: 'US',
      },
      {
        name: 'Pix4D',
        url: 'https://www.pix4d.com/',
        description: 'Photogrammetry and drone mapping software',
        hqCountry: 'CH',
      },
      {
        name: 'Propeller Aero',
        url: 'https://www.propelleraero.com/',
        description: 'Drone survey platform with AeroPoints',
        hqCountry: 'AU',
      },
      {
        name: 'OpenDroneMap',
        url: 'https://www.opendronemap.org/',
        description: 'Free open-source drone image processing',
        hqCountry: 'US',
      },
      {
        name: 'Esri ArcGIS Drone2Map',
        url: 'https://www.esri.com/en-us/arcgis/products/arcgis-drone2map',
        description: 'Rapid 2D/3D mapping from drone imagery',
        hqCountry: 'US',
      },
    ],
  },
  {
    id: 'flight-planning',
    label: 'Flight Planning & Control',
    icon: '\uD83D\uDEE9\uFE0F',
    entries: [
      {
        name: 'DroneHarmony',
        url: 'https://www.droneharmony.com/',
        description: '3D flight planning for inspections',
        hqCountry: 'CH',
      },
      {
        name: 'Hammer Missions',
        url: 'https://www.hammermissions.com/',
        description: 'AI drone inspection and 3D mapping software',
        hqCountry: 'GB',
      },
      {
        name: 'Litchi',
        url: 'https://flylitchi.com/',
        description: 'Autonomous flight app for DJI drones',
        hqCountry: 'GB',
      },
      {
        name: 'Dronelink',
        url: 'https://www.dronelink.com/',
        description: 'Automated flight control for DJI and Autel',
        hqCountry: 'US',
      },
      {
        name: 'Auterion',
        url: 'https://auterion.com/',
        description: 'Open-source drone software platform',
        hqCountry: 'US',
      },
      {
        name: 'Arda',
        url: 'https://www.arda.aero/',
        description: 'Drone connectivity and flight management',
        hqCountry: 'GB',
      },
    ],
  },
  {
    id: 'data-platforms',
    label: 'Data & Analytics Platforms',
    icon: '\uD83D\uDCCA',
    entries: [
      {
        name: 'OpenAerialMap',
        url: 'https://openaerialmap.org/',
        description: 'Open commons for sharing drone/satellite imagery',
        hqCountry: 'US',
      },
      {
        name: 'GeoNadir',
        url: 'https://geonadir.com/',
        description: 'Global drone data repository and processing',
        hqCountry: 'AU',
      },
      {
        name: 'Humanitarian OpenStreetMap',
        url: 'https://www.hotosm.org/',
        description: 'Open mapping for humanitarian response',
        hqCountry: 'US',
      },
      {
        name: 'Raptor Maps',
        url: 'https://raptormaps.com/',
        description: 'Solar farm drone inspection analytics',
        hqCountry: 'US',
      },
      {
        name: 'Zeitview',
        url: 'https://zeitview.com/',
        description: 'Aerial intelligence for infrastructure',
        hqCountry: 'US',
      },
    ],
  },
  {
    id: 'enterprise-software',
    label: 'Enterprise & Fleet Management',
    icon: '\uD83D\uDCBB',
    entries: [
      {
        name: 'Agremo',
        url: 'https://www.agremo.com/',
        description: 'AI-powered drone data analysis for agriculture',
        hqCountry: 'RS',
      },
      {
        name: 'Solvi',
        url: 'https://solvi.earth/',
        description: 'Drone analytics for mining and agriculture',
        hqCountry: 'BR',
      },
      {
        name: 'Consortiq',
        url: 'https://consortiq.com/',
        description: 'Drone program management and LAANC',
        hqCountry: 'US',
      },
      {
        name: 'SkySpecs',
        url: 'https://skyspecs.com/',
        description: 'Automated wind turbine blade inspection',
        hqCountry: 'US',
      },
      {
        name: 'Elsight (Halo)',
        url: 'https://elsight.com/',
        description: 'Multi-network drone connectivity platform',
        hqCountry: 'IL',
      },
    ],
  },
  {
    id: 'hot-osm-tools',
    label: 'HOT / OpenStreetMap Tools',
    icon: '\uD83C\uDF0D',
    entries: [
      {
        name: 'HOT Tasking Manager',
        url: 'https://tasks.hotosm.org/',
        description: 'Coordinate volunteer mapping campaigns — split areas into tasks for remote mappers',
      },
      {
        name: 'fAIr — AI-Assisted Mapping',
        url: 'https://fair-dev.hotosm.org/',
        description: 'AI models for detecting buildings, roads, and features from satellite/drone imagery',
      },
      {
        name: 'Field Tasking Manager (FMTM)',
        url: 'https://fmtm.hotosm.org/',
        description: 'Mobile app for coordinating on-the-ground field mapping and data collection with ODK',
      },
      {
        name: 'MapSwipe',
        url: 'https://mapswipe.org/',
        description: 'Mobile app for crowdsourced imagery validation — swipe to confirm map features',
      },
      {
        name: 'OpenStreetMap iD Editor',
        url: 'https://www.openstreetmap.org/edit',
        description: 'Browser-based OSM editor — trace features from drone orthomosaics into OpenStreetMap',
      },
      {
        name: 'JOSM',
        url: 'https://josm.openstreetmap.de/',
        description: 'Advanced desktop OSM editor with plugin support and imagery layer backgrounds',
      },
      {
        name: 'Overpass Turbo',
        url: 'https://overpass-turbo.eu/',
        description: 'Query and extract OpenStreetMap data by type, tag, or geographic area',
      },
    ],
  },
  {
    id: 'open-source-gis',
    label: 'Open Source GIS & Analysis',
    icon: '\uD83D\uDDA5\uFE0F',
    entries: [
      {
        name: 'QGIS',
        url: 'https://qgis.org/',
        description: 'Free desktop GIS — the industry standard for analysing drone orthomosaics, DEMs, and vector data',
      },
      {
        name: 'GRASS GIS',
        url: 'https://grass.osgeo.org/',
        description: 'Advanced raster/vector processing engine with 400+ analysis modules',
      },
      {
        name: 'PostGIS',
        url: 'https://postgis.net/',
        description: 'Spatial database extension for PostgreSQL — store and query large drone survey datasets',
      },
      {
        name: 'GeoServer',
        url: 'https://geoserver.org/',
        description: 'Serve drone orthomosaics and vector data via WMS/WFS/WCS web standards',
      },
      {
        name: 'GeoNode',
        url: 'https://geonode.org/',
        description: 'Web platform for sharing, managing, and visualising geospatial datasets',
      },
      {
        name: 'PDAL',
        url: 'https://pdal.io/',
        description: 'Point cloud processing library — filter, classify, and transform LiDAR/drone point clouds',
      },
      {
        name: 'Potree',
        url: 'https://potree.github.io/',
        description: 'WebGL viewer for massive 3D point clouds in the browser — share drone survey results',
      },
      {
        name: 'CesiumJS',
        url: 'https://cesium.com/cesiumjs/',
        description: 'Open-source 3D globe framework — visualise drone DEMs, 3D models, and terrain',
      },
      {
        name: 'Cloud Optimized GeoTIFF (COG)',
        url: 'https://cogeo.org/',
        description: 'Format for efficient cloud-based access to drone orthoimagery without full downloads',
      },
      {
        name: 'STAC — SpatioTemporal Asset Catalog',
        url: 'https://stacspec.org/',
        description: 'Standard for cataloguing drone and satellite imagery — searchable by time and location',
      },
    ],
  },
  {
    id: 'web-mapping-viz',
    label: 'Web Mapping & Visualisation',
    icon: '\uD83D\uDDFA\uFE0F',
    entries: [
      {
        name: 'Leaflet.js',
        url: 'https://leafletjs.com/',
        description: 'Lightweight JavaScript library for interactive web maps — embed drone data on any website',
      },
      {
        name: 'OpenLayers',
        url: 'https://openlayers.org/',
        description: 'Feature-rich JavaScript mapping library with full OGC standards support',
      },
      {
        name: 'Kepler.gl',
        url: 'https://kepler.gl/',
        description: 'WebGL data visualisation tool — render millions of drone survey points interactively',
      },
      {
        name: 'deck.gl',
        url: 'https://deck.gl/',
        description: 'High-performance WebGL2 framework for large-scale geospatial data layers',
      },
      {
        name: 'Felt',
        url: 'https://felt.com/',
        description: 'Collaborative cloud-native web GIS — real-time team mapping and analysis',
      },
      {
        name: 'Mapillary',
        url: 'https://www.mapillary.com/',
        description: 'Crowdsourced street-level imagery platform — supplement drone data with ground views',
      },
    ],
  },
];

// ============================================================================
// 5. EDUCATION SECTIONS — Courses, Webinars, and Training Programmes
// ============================================================================

export const EDUCATION_SECTIONS: ResourceSection[] = [
  {
    id: 'online-courses',
    label: 'Online Courses',
    icon: '\uD83C\uDF93',
    entries: [
      // ── African first ──
      {
        name: 'africanDRONE | Introduction to Drone Journalism',
        url: 'https://courses.academy.africa/courses/drone-journalism/',
        description: 'Drone piloting, ethics & newsroom integration',
        hqCountry: 'ZA',
      },
      {
        name: 'ADDA | Certificate of Drone and Data Technology Level 1',
        url: 'https://adda.cired.vt.edu/',
        description: 'UNICEF-backed drone certification in Malawi',
        hqCountry: 'MW',
      },
      {
        name: 'ADDA | RPA Technology',
        url: 'https://adda.cired.vt.edu/',
        description: 'Remotely piloted aircraft technology course',
        hqCountry: 'MW',
      },
      {
        name: 'ADDA | Data Visualization and Cartography',
        url: 'https://adda.cired.vt.edu/',
        description: 'Drone data visualization and mapping course',
        hqCountry: 'MW',
      },
      {
        name: 'EO AFRICA Space Academy',
        url: 'https://www.eoafrica-rd.org/space-academy/on-line-courses/',
        description: 'ESA-funded Earth Observation courses — remote sensing, cloud computing, food security',
        hqCountry: 'TZ',
      },
      // ── Global ──
      {
        name: 'GIS / Storymaps MOOC',
        url: 'https://storymaps.arcgis.com/stories/9bb3b38bd41c4e2ea43cc0a230311935',
        description: 'ESRI course: Earth Observation data as actionable maps',
        hqCountry: 'US',
      },
      {
        name: 'Earth Observation MOOCs (ESA)',
        url: 'https://eo4society.esa.int/training-education/massive-open-online-courses-moocs/',
        description: 'ESA free courses on EO technologies and remote sensing',
        hqCountry: 'EU',
      },
      {
        name: 'FAA Remote Pilot Study Guide',
        url: 'https://www.faa.gov/sites/faa.gov/files/regulations_policies/handbooks_manuals/aviation/remote_pilot_study_guide.pdf',
        description: 'Official FAA Part 107 sUAS study guide (free PDF)',
        hqCountry: 'US',
      },
      {
        name: 'FAA Online Drone Piloting Course',
        url: 'https://www.faasafety.gov/gslac/ALC/course_catalog.aspx',
        description: 'Free FAA ALC-451 sUAS course with video modules',
        hqCountry: 'US',
      },
      {
        name: 'WeRobotics | Drones in Humanitarian Action',
        url: 'https://werobotics.org/courses/',
        description: 'Humanitarian drone deployment course',
        hqCountry: 'CH',
      },
      {
        name: 'WeRobotics | Community Engagement',
        url: 'https://werobotics.org/courses/',
        description: 'Community engagement for drone operations',
        hqCountry: 'CH',
      },
      {
        name: 'WeRobotics | Intro to Youth Training with Aerial Drones',
        url: 'https://werobotics.org/courses/',
        description: 'Youth drone training programme guide',
        hqCountry: 'CH',
      },
      {
        name: 'DARTdrones | Disaster Response Drone Training',
        url: 'https://www.dartdrones.com/disaster-response/',
        description: 'Emergency and disaster drone pilot course',
        hqCountry: 'US',
      },
      {
        name: 'Women Who Drone | Drone Academy',
        url: 'https://www.womenwhodrone.com/academy',
        description: 'Drone training for women pilots',
        hqCountry: 'US',
      },
    ],
  },
  {
    id: 'webinars',
    label: 'Webinars & Workshops',
    icon: '\uD83C\uDF99\uFE0F',
    entries: [
      // ── African first ──
      {
        name: 'africanDRONE Drone Camps',
        url: 'https://africandrone.org/drone-events',
        description: 'In-person drone camps and events across Africa',
        hqCountry: 'ZA',
      },
      {
        name: 'Flying Labs | Drone Applications for Disaster Management',
        url: 'https://flyinglabs.org/',
        description: 'Flying Labs webinar on disaster drones',
        hqCountry: 'ZA',
      },
      // ── Global ──
      {
        name: 'Esri | Improving Response Coordination with GIS and UAVs',
        url: 'https://www.esri.com/en-us/lg/industry/public-safety/improving-response-coordination',
        description: 'GIS and drone disaster response webinar',
        hqCountry: 'US',
      },
      {
        name: 'Esri | UAS and Remote Sensing at the Surfside Collapse',
        url: 'https://www.esri.com/',
        description: 'Drone remote sensing for disaster recovery',
        hqCountry: 'US',
      },
    ],
  },
  {
    id: 'training-providers',
    label: 'Training Providers',
    icon: '\uD83C\uDFEB',
    entries: [
      // ── African first ──
      {
        name: 'africanDRONE Academy',
        url: 'https://africandrone.org/academy',
        description: 'Drone journalism and pilot training courses',
        hqCountry: 'ZA',
      },
      {
        name: 'African Drone and Data Academy (ADDA)',
        url: 'https://adda.cired.vt.edu/',
        description: 'UNICEF-powered drone training in Malawi',
        hqCountry: 'MW',
      },
      {
        name: 'Drone Space Kenya',
        url: 'https://dronespace.co.ke/',
        description: 'KCAA-approved drone pilot license training',
        hqCountry: 'KE',
      },
      {
        name: 'Ntsu Drone Academy',
        url: 'https://www.ntsuaviation.com/',
        description: 'SACAA-accredited drone pilot training',
        hqCountry: 'ZA',
      },
      {
        name: 'Drones for Africa',
        url: 'https://www.dronesforafrica.com/',
        description: 'Botswana drone training and STEM education',
        hqCountry: 'BW',
      },
      {
        name: 'KENDRONE',
        url: 'https://www.kendrone.co.ke/',
        description: 'Kenya\'s first KCAA-approved drone org',
        hqCountry: 'KE',
      },
      // ── Global ──
      {
        name: 'DARTdrones',
        url: 'https://www.dartdrones.com/',
        description: 'Nationwide drone training in 40+ US cities',
        hqCountry: 'US',
      },
      {
        name: 'Drone U',
        url: 'https://www.thedroneu.com/',
        description: 'Online and in-person pro drone courses',
        hqCountry: 'US',
      },
      {
        name: 'UAV Coach',
        url: 'https://uavcoach.com/',
        description: 'Part 107 prep, TRUST, and drone training',
        hqCountry: 'US',
      },
      {
        name: 'Women Who Drone',
        url: 'https://www.womenwhodrone.com/',
        description: 'Drone academy and women-in-drones community',
        hqCountry: 'US',
      },
      {
        name: 'Embry-Riddle Aeronautical University',
        url: 'https://erau.edu/degrees/bachelor/uncrewed-autonomous-systems',
        description: 'First accredited UAS degree program in the US',
        hqCountry: 'US',
      },
    ],
  },
];

// ============================================================================
// 6. NEWS FEEDS — heavily weighted toward African drone news
// ============================================================================

export const NEWS_FEEDS: NewsFeed[] = [
  // ── African-focused feeds first ──
  {
    id: 'africandrone',
    label: 'africanDRONE',
    url: 'https://medium.com/feed/africandrone-organization',
    color: '#10b981',
  },
  // ── Global feeds ──
  {
    id: 'dronelife',
    label: 'DroneLife',
    url: 'https://dronelife.com/feed/',
    color: '#2563eb',
  },
  {
    id: 'suas',
    label: 'sUAS News',
    url: 'https://www.suasnews.com/feed/',
    color: '#d97706',
  },
  {
    id: 'uasweekly',
    label: 'UAS Weekly',
    url: 'https://uasweekly.com/feed/',
    color: '#7c3aed',
  },
];

// ============================================================================
// HELPER FUNCTION
// ============================================================================

/**
 * Return every resource entry whose headquarters matches the given
 * ISO 3166-1 alpha-2 country code (case-sensitive, e.g. 'US', 'CN', 'DE').
 */
export function resourcesByCountry(iso2: string): ResourceEntry[] {
  const all = [
    ...SAFETY_SECTIONS,
    ...EQUIPMENT_SECTIONS,
    ...MANUFACTURER_SECTIONS,
    ...SOFTWARE_SECTIONS,
    ...EDUCATION_SECTIONS,
  ].flatMap((s) => s.entries);
  const seen = new Set<string>();
  return all.filter((e) => {
    if (e.hqCountry !== iso2) return false;
    const key = e.name.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
